import { memo, useMemo } from 'react'

import { Box, Section, Text } from '@factorialco/deprecated-design-system'
import { GetLegalEntitiesWithTemplateSettingsDocument } from 'generated/resources/graphql'

import Select from 'modules/core/components/Select'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useUpsertLegalEntityDocumentTemplateSetting } from 'modules/it_management/hooks/useUpsertLegalEntityDocumentTemplateSetting'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

interface LegalEntity {
  id: string
  legalName: string
  country: string
}

interface DocumentTemplate {
  id: string
  title: string
  templateType: string
}

interface TemplateSetting {
  id: string
  legalEntityId: string
  assignedDocumentTemplateId?: string | null
  unassignedDocumentTemplateId?: string | null
}

const LegalEntitiesPage = () => {
  const { company } = useCurrentSession()
  const { data, loading, error } = useQuery(GetLegalEntitiesWithTemplateSettingsDocument)
  const { upsertSetting } = useUpsertLegalEntityDocumentTemplateSetting()

  const legalEntities: LegalEntity[] = data?.companies?.legalEntitiesConnection?.nodes || []
  const documentTemplates: DocumentTemplate[] =
    data?.itManagement?.documentTemplatesConnection?.nodes || []
  const settings: TemplateSetting[] =
    data?.itManagement?.legalEntityDocumentTemplateSettingsConnection?.nodes || []

  // Create a map for quick lookup of settings by legal entity ID
  const settingsByLegalEntity = useMemo(() => {
    const map = new Map<string, TemplateSetting>()
    settings.forEach((setting) => {
      map.set(setting.legalEntityId, setting)
    })
    return map
  }, [settings])

  // Filter templates by type
  const assignedTemplates = useMemo(
    () => documentTemplates.filter((t) => t.templateType === 'assign'),
    [documentTemplates]
  )

  const unassignedTemplates = useMemo(
    () => documentTemplates.filter((t) => t.templateType === 'unassign'),
    [documentTemplates]
  )

  const handleTemplateChange = async (
    legalEntityId: string,
    field: 'assignedDocumentTemplateId' | 'unassignedDocumentTemplateId',
    templateId: string
  ) => {
    const existingSetting = settingsByLegalEntity.get(legalEntityId)
    const otherField =
      field === 'assignedDocumentTemplateId'
        ? 'unassignedDocumentTemplateId'
        : 'assignedDocumentTemplateId'

    const result = await upsertSetting({
      id: existingSetting?.id || null,
      companyId: company.id,
      legalEntityId: legalEntityId,
      [field]: templateId || null,
      [otherField]: existingSetting?.[otherField] || null,
    })

    const { success } = handleResult(result, () => undefined)

    if (success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    }
  }

  return (
    <ResponsivePage.Body loading={loading} error={error} showHeader={false}>
      <Section
        illustration="documents"
        title={i18n.t('it_management.settings.legal_entities.title')}
        description={i18n.t('it_management.settings.legal_entities.description')}
      >
        {legalEntities.length === 0 ? (
          <Box padding="s48" style={{ textAlign: 'center' }}>
            <Text.Normal color="grey600" size="200">
              {i18n.t('it_management.settings.legal_entities.no_legal_entities')}
            </Text.Normal>
          </Box>
        ) : (
          <Box flexDirection="column" gap="s24">
            {legalEntities.map((legalEntity) => {
              const setting = settingsByLegalEntity.get(legalEntity.id)

              return (
                <Box
                  key={legalEntity.id}
                  flexDirection="column"
                  gap="s16"
                  background="grey200"
                  padding="s24"
                  border={{
                    all: {
                      color: 'grey400',
                      width: 's1',
                      style: 'solid',
                    },
                  }}
                  borderRadius={{
                    all: 'abs008',
                  }}
                >
                  <Text.Normal weight="semibold" size="300">
                    {legalEntity.legalName}
                  </Text.Normal>

                  <Box
                    flexDirection="row"
                    gap="s16"
                    style={{
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box
                      flexDirection="column"
                      gap="s8"
                      style={{ flex: '1 1 250px', minWidth: '0' }}
                    >
                      <Text.Normal weight="medium" size="200">
                        {i18n.t('it_management.settings.legal_entities.assigned_template')}
                      </Text.Normal>
                      <Select
                        none={i18n.t('it_management.settings.legal_entities.select_template')}
                        value={setting?.assignedDocumentTemplateId || ''}
                        onChange={(value) =>
                          handleTemplateChange(legalEntity.id, 'assignedDocumentTemplateId', value)
                        }
                        options={assignedTemplates.map((t) => ({
                          value: t.id,
                          label: t.title,
                        }))}
                        missingLabel={i18n.t('common.missing_field')}
                      />
                    </Box>

                    <Box
                      flexDirection="column"
                      gap="s8"
                      style={{ flex: '1 1 250px', minWidth: '0' }}
                    >
                      <Text.Normal weight="medium" size="200">
                        {i18n.t('it_management.settings.legal_entities.unassigned_template')}
                      </Text.Normal>
                      <Select
                        none={i18n.t('it_management.settings.legal_entities.select_template')}
                        value={setting?.unassignedDocumentTemplateId || ''}
                        onChange={(value) =>
                          handleTemplateChange(
                            legalEntity.id,
                            'unassignedDocumentTemplateId',
                            value
                          )
                        }
                        options={unassignedTemplates.map((t) => ({
                          value: t.id,
                          label: t.title,
                        }))}
                        missingLabel={i18n.t('common.missing_field')}
                      />
                    </Box>
                  </Box>
                </Box>
              )
            })}
          </Box>
        )}
      </Section>
    </ResponsivePage.Body>
  )
}

export default memo(LegalEntitiesPage)
